/**
 * TestIt — shared test taxonomy (BL-214).
 *
 * The SINGLE source of truth for which reusable "testing units" exist, how they
 * map into the CheckUp assessment, how long a result stays valid, and the shape
 * of the stored result. Both platform-api (`TalentTestResultsService`,
 * aggregation, reconciliation) and the frontends (TestIt catalog, the modal
 * lead-in) import from here so the catalog can never drift.
 *
 * Follows the passport-catalog pattern (`packages/heroes/src/passport/catalog`)
 * but as a code registry — the entries reference widget ids and CheckUp step
 * keys, which are code concepts, so a TS `as const` registry beats a JSON file.
 *
 * Design note — the reusable UNIT is the whole test (e.g. one English result),
 * not each CEFR skill. Language results carry their CEFR components together in
 * `result.cefr`, matching how `buildCEFRResults` consumes them and how the reuse
 * UX presents them ("reuse your English result — B2"). `subTest` stays available
 * (schema default `''`) for genuinely separable future units.
 */

/**
 * CheckUp step keys a test can satisfy. Mirrors the `stepKey` values in
 * platform-api's `CHECKUP_CANONICAL_STEPS` (checkup.constants.ts). Only the
 * three "test-bearing" steps appear here; `videos_watched` and `assistant_chat`
 * are guide interactions, not reusable tests.
 */
export type CheckupTestStepKey = 'pitch_video' | 'language_tests' | 'motivational_answers';

/** The widget that administers a test (matches the shared widget registry ids). */
export type TestWidgetId = 'pitch-recorder' | 'language-test' | 'motivational';

/** The typed shape of `talent_test_results.result` for a test. */
export type TestResultShape = 'cefr' | 'pitch' | 'motivational';

/** One entry in the test catalog. */
export interface TestCatalogEntry {
  /** Stable registry id — matches `talent_test_results.test_id`. */
  readonly id: string;
  /** i18n key for the test's display name (namespace `testit.*`). */
  readonly labelKey: string;
  /** Reusable sub-units; `[]` for single-unit tests (stored with `sub_test=''`). */
  readonly subTests: readonly string[];
  /** The FE widget that administers this test. */
  readonly widgetId: TestWidgetId;
  /** Which canonical CheckUp step this test satisfies (progress reconciliation). */
  readonly checkupStep: CheckupTestStepKey;
  /**
   * Platform B2C validity window in days; `null` = never expires. A B2B
   * invitation may impose a TIGHTER `maxResultAgeDays` on top of this — the
   * effective validity is the min of the two.
   */
  readonly validityDays: number | null;
  /** The typed shape of the stored result. */
  readonly resultShape: TestResultShape;
  /** Offered as a B2B invitation test. */
  readonly visibleToB2B: boolean;
  /** Offered in the citizen B2C catalog. */
  readonly visibleToB2C: boolean;
}

/**
 * The catalog. Adding a test here makes it flow through the store, the
 * reconciliation, the catalog screen, and the invitation picker automatically.
 */
export const TEST_CATALOG = [
  {
    id: 'pitch',
    labelKey: 'testit.test.pitch',
    subTests: [],
    widgetId: 'pitch-recorder',
    checkupStep: 'pitch_video',
    validityDays: 90,
    resultShape: 'pitch',
    visibleToB2B: true,
    visibleToB2C: true,
  },
  {
    id: 'language.en',
    labelKey: 'testit.test.languageEn',
    subTests: [],
    widgetId: 'language-test',
    checkupStep: 'language_tests',
    validityDays: 180,
    resultShape: 'cefr',
    visibleToB2B: true,
    visibleToB2C: true,
  },
  {
    id: 'language.pt',
    labelKey: 'testit.test.languagePt',
    subTests: [],
    widgetId: 'language-test',
    checkupStep: 'language_tests',
    validityDays: 180,
    resultShape: 'cefr',
    visibleToB2B: true,
    visibleToB2C: true,
  },
  {
    id: 'motivational',
    labelKey: 'testit.test.motivational',
    subTests: [],
    widgetId: 'motivational',
    checkupStep: 'motivational_answers',
    validityDays: 365,
    resultShape: 'motivational',
    visibleToB2B: true,
    visibleToB2C: true,
  },
] as const satisfies readonly TestCatalogEntry[];

export type TestCatalogId = (typeof TEST_CATALOG)[number]['id'];

export const TEST_CATALOG_VERSION = '1.0.0';

/** testId prefix for the language-test family (`language.en`, `language.pt`, …). */
export const LANGUAGE_TEST_PREFIX = 'language.';

/** Platform validity (days) for any language result, offered or not. */
const LANGUAGE_VALIDITY_DAYS = 180;

/** Normalize a human/metadata language token to a stable lowercase code. */
const LANGUAGE_CODE_ALIASES: Record<string, string> = {
  english: 'en',
  en: 'en',
  inglês: 'en',
  ingles: 'en',
  portuguese: 'pt',
  português: 'pt',
  portugues: 'pt',
  pt: 'pt',
  'pt-br': 'pt',
  'pt-pt': 'pt',
  spanish: 'es',
  español: 'es',
  espanol: 'es',
  es: 'es',
};

/** Map a language name/token (e.g. 'English', 'inglês', 'en') to a stable code ('en'). */
export function normalizeLanguageCode(input: string): string {
  const key = input.trim().toLowerCase();
  return LANGUAGE_CODE_ALIASES[key] ?? key;
}

/** Build the store testId for a language (e.g. 'English' → 'language.en'). */
export function languageTestId(input: string): string {
  return `${LANGUAGE_TEST_PREFIX}${normalizeLanguageCode(input)}`;
}

/** The language a language test is in ('language.es' → 'es'); `null` for any other test. */
export function languageCodeOfTest(testId: string): string | null {
  return testId.startsWith(LANGUAGE_TEST_PREFIX) && testId.length > LANGUAGE_TEST_PREFIX.length
    ? testId.slice(LANGUAGE_TEST_PREFIX.length)
    : null;
}

// ── Lookups ────────────────────────────────────────────────────────────────

/** Look up an OFFERABLE catalog entry by exact test id (for the B2B/B2C catalog UI). */
export function getTestCatalogEntry(testId: string): TestCatalogEntry | undefined {
  return TEST_CATALOG.find((t) => t.id === testId);
}

/**
 * Resolve a descriptor for ANY stored test id — including languages not in the
 * offerable catalog (e.g. a native-language CEFR result like `language.es`).
 * Such results are storable + scorable but not offered as standalone tests
 * (`visibleToB2B/C = false`). Returns undefined for genuinely unknown ids.
 *
 * Use this for recording/reading the store; use `getTestCatalogEntry` for the
 * offerable-catalog UI.
 */
export function resolveTest(testId: string): TestCatalogEntry | undefined {
  const entry = getTestCatalogEntry(testId);
  if (entry) return entry;
  if (testId.startsWith(LANGUAGE_TEST_PREFIX) && testId.length > LANGUAGE_TEST_PREFIX.length) {
    return {
      id: testId,
      labelKey: 'testit.test.language',
      subTests: [],
      widgetId: 'language-test',
      checkupStep: 'language_tests',
      validityDays: LANGUAGE_VALIDITY_DAYS,
      resultShape: 'cefr',
      visibleToB2B: false,
      visibleToB2C: false,
    };
  }
  return undefined;
}

/** All test ids that satisfy a given CheckUp step (e.g. both languages → `language_tests`). */
export function getTestsForCheckupStep(step: CheckupTestStepKey): TestCatalogEntry[] {
  return TEST_CATALOG.filter((t) => t.checkupStep === step);
}

// ── B2B invitation helpers (TestIt "specific tests" selection) ──────────────

/** The offerable B2B tests — the exact set a company can pick for a TestIt invite. */
export function getB2BTests(): TestCatalogEntry[] {
  return TEST_CATALOG.filter((t) => t.visibleToB2B);
}

/** True when `testId` is an offerable B2B test (used to validate an invitation). */
export function isOfferableB2BTest(testId: string): boolean {
  return getTestCatalogEntry(testId)?.visibleToB2B === true;
}

/**
 * The set of CheckUp steps satisfied by a chosen list of test ids — the
 * completion gate for a `testit` invitation. Unknown ids are ignored (they
 * contribute no step). An empty input yields an empty set; callers that want
 * the legacy "full TestIt bundle" fallback must special-case an empty list.
 */
export function checkupStepsForTests(testIds: readonly string[]): Set<CheckupTestStepKey> {
  const steps = new Set<CheckupTestStepKey>();
  for (const id of testIds) {
    const entry = resolveTest(id);
    if (entry) steps.add(entry.checkupStep);
  }
  return steps;
}

/**
 * English canonical display names for the reusable tests, keyed by catalog id.
 * Used to build the English `{{testit.tests}}` email tag (email copy is
 * authored in English + localized on read). Kept here so both invitation send
 * paths format the list identically. Mirrors the `testit.test.*` UI labels.
 */
const TESTIT_TEST_NAMES_EN: Readonly<Record<string, string>> = {
  pitch: 'Pitch Video',
  'language.en': 'English',
  'language.pt': 'Portuguese',
  motivational: 'Motivational',
};

/** English display name for a test id (falls back to the raw id). */
export function testitTestEnglishName(testId: string): string {
  return TESTIT_TEST_NAMES_EN[testId] ?? testId;
}

/** Comma-join the chosen tests' English display names, in catalog order. */
export function formatTestitTestNamesEn(testIds: readonly string[]): string {
  return testIds.map(testitTestEnglishName).join(', ');
}

// ── Validity (backend-computed — never do this date math in a .tsx) ─────────

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Compute a result's expiry from when it was taken and the test's platform
 * validity. Returns `null` when the test never expires.
 */
export function computeExpiresAt(takenAt: Date, entry: TestCatalogEntry): Date | null {
  if (entry.validityDays == null) return null;
  return new Date(takenAt.getTime() + entry.validityDays * MS_PER_DAY);
}

/** True when a result is still valid at `now` (no expiry = always valid). */
export function isResultValid(expiresAt: Date | null | undefined, now: Date = new Date()): boolean {
  if (expiresAt == null) return true;
  return expiresAt.getTime() > now.getTime();
}

/**
 * Can this banked result stand IN PLACE OF taking the test again?
 *
 * Deliberately not the same question as `isResultValid`. Validity is about the
 * clock. Reusability is about whether there is anything behind the result: a
 * SELF-DECLARED level (`unverified`) has no analyzed artifact, so reusing it
 * would complete the step with nothing for MESH to score, and would present a
 * declaration as the assessment it is standing in for — the thing BL-148 exists
 * to prevent.
 *
 * A declaration stays perfectly valid as a declaration; the catalog is right to
 * show it. It just cannot be the answer to "have you taken this test".
 */
export function isResultReusable(
  result: { unverified?: boolean } | null | undefined,
  expiresAt: Date | null | undefined,
  now: Date = new Date()
): boolean {
  if (!isResultValid(expiresAt, now)) return false;
  return result?.unverified !== true;
}

/**
 * Does a banked result SATISFY the test it records? (BL-148)
 *
 * The one answer to "has this person taken this test", for every place that
 * asks it: a CheckUp step (the assessment read and progress reconcile), an
 * invited TestIt test (the citizen's assignment and the company's Vetting
 * overview), and a new route's CheckUp seed (F31). Those used to answer with
 * `isValid` in some places and `isReusable` in one, so a SELF-DECLARED language
 * level — valid as a declaration, with nothing behind it for MESH to score —
 * completed the language step and marked an invited language test done.
 *
 * Takes the backend-computed view (`TalentTestResultsService`), whose
 * `isReusable` is {@link isResultReusable}: inside its validity window AND not
 * self-declared.
 */
export function resultSatisfiesTest(result: { readonly isReusable: boolean }): boolean {
  return result.isReusable;
}

/** The CheckUp steps a set of banked results satisfies — see {@link resultSatisfiesTest}. */
export function checkupStepsSatisfiedBy(
  results: ReadonlyArray<{ readonly testId: string; readonly isReusable: boolean }>
): Set<CheckupTestStepKey> {
  const steps = new Set<CheckupTestStepKey>();
  for (const r of results) {
    if (!resultSatisfiesTest(r)) continue;
    const entry = resolveTest(r.testId);
    if (entry) steps.add(entry.checkupStep);
  }
  return steps;
}

/**
 * Whole days remaining until expiry (0 when expired; `null` when it never
 * expires). Rounded up so "expires today" reads as a positive number until the
 * instant it lapses.
 */
export function daysRemaining(
  expiresAt: Date | null | undefined,
  now: Date = new Date()
): number | null {
  if (expiresAt == null) return null;
  const ms = expiresAt.getTime() - now.getTime();
  if (ms <= 0) return 0;
  return Math.ceil(ms / MS_PER_DAY);
}

/**
 * The effective expiry for a result under a B2B invitation: the earlier of the
 * platform validity and the invitation's `maxResultAgeDays` (measured from
 * `takenAt`). Used to decide whether a stored result satisfies an invited test.
 */
export function effectiveExpiresAt(
  takenAt: Date,
  entry: TestCatalogEntry,
  maxResultAgeDays: number | null | undefined
): Date | null {
  const platform = computeExpiresAt(takenAt, entry);
  if (maxResultAgeDays == null) return platform;
  const invite = new Date(takenAt.getTime() + maxResultAgeDays * MS_PER_DAY);
  if (platform == null) return invite;
  return platform.getTime() < invite.getTime() ? platform : invite;
}
